import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';

import { getCertificateByIdAsync } from '@share/api';
import type { ErrorResponse, ICertificateDetails } from '@share/common-types';

export type ICertificateByIdError = Pick<ErrorResponse, 'title' | 'code'>;

export interface ICertificateByIdState {
  certificate: ICertificateDetails | null;
  loading: boolean;
  error: ICertificateByIdError | null;
}

const initialState: ICertificateByIdState = {
  certificate: null,
  loading: false,
  error: null,
};

function parseCertificateByIdError(err: unknown): ICertificateByIdError {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data;
    if (data && typeof data === 'object' && 'title' in data && 'code' in data) {
      const { title, code } = data as { title?: unknown; code?: unknown };
      if (typeof title === 'string' && typeof code === 'string') {
        return { title, code };
      }
    }
  }
  if (err instanceof Error) {
    return { title: err.message, code: 'request_failed' };
  }
  return { title: 'Unknown error', code: 'unknown_error' };
}

export const fetchCertificateById = createAsyncThunk<
  ICertificateDetails,
  { id: string; clubSiteId?: number },
  { rejectValue: ICertificateByIdError }
>('CertificateById/fetchById', async ({ id, clubSiteId }, { rejectWithValue }) => {
  try {
    const { data } = await getCertificateByIdAsync(id, clubSiteId);
    return data;
  } catch (err: unknown) {
    return rejectWithValue(parseCertificateByIdError(err));
  }
});

const CertificateByIdSlice = createSlice({
  name: 'CertificateById',
  initialState,
  reducers: {
    clearCertificate: (state: ICertificateByIdState) => {
      state.certificate = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCertificateById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCertificateById.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.certificate = payload;
        state.error = null;
      })
      .addCase(fetchCertificateById.rejected, (state, { payload }) => {
        state.loading = false;
        state.certificate = null;
        state.error = payload ?? { title: 'Unknown error', code: 'unknown_error' };
      });
  },
});

export const CertificateByIdActions = CertificateByIdSlice.actions;

export const CertificateByIdReducer = CertificateByIdSlice.reducer;
